import { useState, useMemo } from 'react'
import { serviceTotal, tax } from '../../AddServiceForm/typings'
import { currencyString } from '@sales-center/common'
import { ENTITY_TYPES } from '@sales-center/common'
import { useAppSelector } from '@user/store/hooks'
import { selectLeadDetail } from '@user/store/lead'
import { selectOpportunityDetail } from '@user/store/opportunity'
import { DialogState, ServiceCardProps } from '../typings'
import { useSchedulingEnabled } from '@user/store/scheduling'

export function useData(
  props: Pick<ServiceCardProps, 'service' | 'index' | 'entityType'>,
) {
  const { service, index, entityType } = props

  const {
    quantity,
    initialPrice,
    initialDiscountAmount,
    initialDiscountType,
    recurringPrice,
    recurringDiscountAmount,
    recurringDiscountType,
    taxRate,
    annualOccurrences,
    appointmentEntityId,
    estimateEntityId,
  } = service

  const serviceNumber = index + 1

  const [dialog, setDialog] = useState<DialogState>()

  const [detailsOpen, setDetailsOpen] = useState<boolean>(false)

  const detail = useAppSelector((state) =>
    entityType === ENTITY_TYPES.Lead
      ? selectLeadDetail(state)
      : selectOpportunityDetail(state),
  )

  const initialDiscount = useMemo(
    () =>
      initialDiscountType === 'dollar'
        ? `$${currencyString(initialDiscountAmount)}`
        : `${initialDiscountAmount * 100}%`,
    [initialDiscountAmount, initialDiscountType],
  )

  const recurringDiscount = useMemo(
    () =>
      recurringDiscountType === 'dollar'
        ? `$${currencyString(recurringDiscountAmount)}`
        : `${recurringDiscountAmount * 100}%`,
    [recurringDiscountAmount, recurringDiscountType],
  )

  const totalTax = useMemo(() => {
    const total = serviceTotal(
      initialPrice -
        (initialDiscountType === 'dollar'
          ? initialDiscountAmount
          : initialPrice * initialDiscountAmount),
      recurringPrice -
        (recurringDiscountType === 'dollar'
          ? recurringDiscountAmount
          : recurringPrice * recurringDiscountAmount),
      annualOccurrences,
      quantity,
    )

    const totalTax = tax(total, taxRate)
    return `$${currencyString(totalTax)}`
  }, [
    annualOccurrences,
    initialPrice,
    quantity,
    recurringPrice,
    taxRate,
    initialDiscountType,
    initialDiscountAmount,
    recurringDiscountType,
    recurringDiscountAmount,
  ])

  const schedulingEnabled = useSchedulingEnabled()

  const showApptWarning =
    (estimateEntityId !== null || appointmentEntityId !== null) &&
    schedulingEnabled

  const viewDetailsButtonText = detailsOpen ? 'Hide Details' : 'Show Details'

  return {
    detailsOpen,
    setDetailsOpen,
    dialog,
    totalTax,
    setDialog,
    serviceNumber,
    initialDiscount,
    showApptWarning,
    recurringDiscount,
    detailId: detail?.id,
    viewDetailsButtonText,
  }
}
export type UseData = ReturnType<typeof useData>
